import { surfaceAuthCanvas, surfaceAuthCard } from '@/theme/designTokens'
import { SP_SIGNUP_NAVY, SP_SIGNUP_YELLOW, SP_SIGNUP_YELLOW_HOVER } from './signupShareTheme'
import {
  surveyEntryHeaderNavy,
  surveyEntryHeadingColor,
  surveyEntryMutedColor,
} from './surveyEntryStyles'
import {
  SURVEY_COMPLETE_BUTTON_WIDTH_PX,
  SURVEY_COMPLETE_CARD_MAX_WIDTH_PX,
  SURVEY_COMPLETE_CARD_PADDING_PX,
  SURVEY_COMPLETE_CARD_VERTICAL_INSET_PX,
  SURVEY_COMPLETE_ILLUSTRATION_HEIGHT_PX,
  SURVEY_COMPLETE_ILLUSTRATION_MAX_WIDTH_PX,
  SURVEY_COMPLETE_INNER_MAX_WIDTH_PX,
  SURVEY_ENTRY_CARD_RADIUS_PX,
} from './surveyEntryLayoutConstants'
import {
  SURVEY_EXEC_CONTENT_MAX_WIDTH_PX,
  SURVEY_EXEC_HEADER_BAND_HEIGHT_PX,
  SURVEY_EXEC_HERO_CONTENT_PADDING_TOP_PX,
  SURVEY_EXEC_PROGRESS_SECTION_GAP_PX,
  SURVEY_EXEC_QUESTION_CARD_MIN_HEIGHT_PX,
  SURVEY_EXEC_SECTION_GAP_PX,
  SURVEY_EXEC_WORKSPACE_TOP_GAP_PX,
  SURVEY_QUESTION_CARD_PADDING_BOTTOM_PX,
  SURVEY_QUESTION_CARD_PADDING_TOP_PX,
  SURVEY_QUESTION_CARD_PADDING_X_PX,
  SURVEY_QUESTION_EYEBROW_TO_PROMPT_GAP_PX,
  SURVEY_QUESTION_PROMPT_TO_ANSWERS_GAP_PX,
} from './surveyExecutionLayoutConstants'

/**
 * Survey accent. Guest sign-up screens set `--sp-survey-accent` to marketing `#FF9933`;
 * logged-in / campaign surveys keep B.Primary 1 `#008CD0`.
 */
export const surveyExecPrimary = 'var(--sp-survey-accent, #008cd0)'
/** Text on accent fill — navy on guest yellow, white on logged-in blue. */
export const surveyExecOnAccent = 'var(--sp-survey-on-accent, #ffffff)'
/** Completion “Congratulations” copy stays B.Primary 1, even on guest sign-up orange screens. */
export const surveyExecBrandBlue = '#008cd0'
export const surveyExecBorder = '#e2e8f0'
export const surveyExecOptionHover = '#f0f8fd'
export const surveyExecProgressTrack = '#dce9ff'
export const surveyExecHeadingNavy = '#04183f'
export const surveyExecPromptText = '#0f172a'
export const surveyExecOptionLabel = '#0d1b2a'
export const surveyExecEyebrow = '#6b8fa3'
export const surveyExecRequired = '#ff5959'
export const surveyExecYesNoSelectedBorder = '#e0e6ea'
export const surveyExecHeaderTitleOnNavy = '#f8f9ff'
export const surveyExecNavPrevious = '#4d5d88'
export const surveyExecEtaMuted = '#6f7881'

/** Figma 5074-13248 — full-width execution route (no info card / logo). */
export const surveyExecutionFrameSx = {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  boxSizing: 'border-box',
} as const

/** Figma 5074-13259 — centered 768px column (256px inset at 1280 ref). */
export const surveyExecutionColumnSx = {
  width: '100%',
  maxWidth: SURVEY_EXEC_CONTENT_MAX_WIDTH_PX,
  mx: 'auto',
  alignSelf: 'center',
  boxSizing: 'border-box',
  px: { xs: 2, sm: 0 },
} as const

/**
 * Figma 5074-13249 — full-width navy band (285px), flush to layout top.
 * Use `width: 100%` only — `100vw` + `calc(50% - 50vw)` bleeds past the document when a
 * vertical scrollbar is present (`100vw` includes the scrollbar gutter), causing horizontal scroll.
 */
export const surveyExecutionHeroBleedSx = {
  width: '100%',
  alignSelf: 'stretch',
  minHeight: SURVEY_EXEC_HEADER_BAND_HEIGHT_PX,
  bgcolor: surveyEntryHeaderNavy,
  backgroundColor: surveyEntryHeaderNavy,
  flexShrink: 0,
  boxSizing: 'border-box',
  pt: `${SURVEY_EXEC_HERO_CONTENT_PADDING_TOP_PX}px`,
  pb: { xs: 2.5, sm: 3 },
} as const

/** Figma 5074-13253 / 13254 — 768px column, left-aligned title + subtitle */
export const surveyExecutionHeroZoneSx = {
  width: '100%',
  maxWidth: SURVEY_EXEC_CONTENT_MAX_WIDTH_PX,
  mx: 'auto',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'stretch',
  boxSizing: 'border-box',
  px: { xs: 2, sm: 0 },
} as const

/** Progress + card shell + nav — light canvas; Figma 5530-11041 starts at y=309. */
export const surveyExecutionWorkspaceSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: `${SURVEY_EXEC_SECTION_GAP_PX}px`,
  width: '100%',
  mt: `${SURVEY_EXEC_WORKSPACE_TOP_GAP_PX}px`,
  boxSizing: 'border-box',
  flexShrink: 0,
} as const

/** Figma 5530-11036 — hero text over navy band */
export const surveyExecutionHeaderBlockSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '14.9px',
  width: '100%',
  maxWidth: '700px',
} as const

export const surveyExecutionHeaderTitleSx = {
  m: 0,
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: { xs: '28px', sm: '36px', md: '40px' },
  lineHeight: { xs: '36px', sm: '40px', md: '40px' },
  letterSpacing: '0.4063px',
  color: surveyExecHeaderTitleOnNavy,
} as const

export const surveyExecutionHeaderSubtitleSx = {
  m: 0,
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: '18px',
  lineHeight: '28px',
  color: surveyExecPrimary,
  maxWidth: '672px',
} as const

export const surveyProgressSectionSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: `${SURVEY_EXEC_PROGRESS_SECTION_GAP_PX}px`,
  width: '100%',
} as const

export const surveyProgressMetaRowSx = {
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  gap: 2,
  width: '100%',
} as const

export const surveyProgressLabelSx = {
  m: 0,
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: '20px',
  lineHeight: '28px',
  color: surveyExecHeadingNavy,
} as const

export const surveyProgressEtaSx = {
  m: 0,
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: '12px',
  lineHeight: '16px',
  letterSpacing: '0.12px',
  color: surveyExecEtaMuted,
  textAlign: 'right',
} as const

export const surveyProgressEtaHighlightSx = {
  color: surveyExecPrimary,
} as const

export const surveyProgressTrackSx = {
  width: '100%',
  height: '8px',
  borderRadius: '12px',
  bgcolor: surveyExecProgressTrack,
  overflow: 'hidden',
} as const

export const surveyProgressFillSx = {
  height: '100%',
  borderRadius: '12px',
  bgcolor: surveyExecPrimary,
  transition: 'width 0.2s ease',
} as const

/** Figma 5074-13194 / 5530-11050 — white card chrome */
export const surveyQuestionCardShellSx = {
  width: '100%',
  minHeight: SURVEY_EXEC_QUESTION_CARD_MIN_HEIGHT_PX,
  bgcolor: surfaceAuthCard,
  backgroundColor: surfaceAuthCard,
  borderRadius: '20px',
  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
} as const

/** Figma 5074-13194 — inner padding + vertical rhythm (eyebrow → prompt → answers) */
export const surveyQuestionCardInnerSx = {
  flex: '1 1 auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  boxSizing: 'border-box',
  width: '100%',
  px: { xs: 2.5, sm: `${SURVEY_QUESTION_CARD_PADDING_X_PX}px` },
  pt: { xs: 2.5, sm: `${SURVEY_QUESTION_CARD_PADDING_TOP_PX}px` },
  pb: { xs: 3, sm: `${SURVEY_QUESTION_CARD_PADDING_BOTTOM_PX}px` },
} as const

/** Figma 5074-13197 — answer controls slot below prompt */
export const surveyQuestionAnswersSx = {
  width: '100%',
  mt: { xs: 3, sm: `${SURVEY_QUESTION_PROMPT_TO_ANSWERS_GAP_PX}px` },
  boxSizing: 'border-box',
} as const

/** When a description sits between prompt and answers — gap comes from description `mb`. */
export const surveyQuestionAnswersAfterDescriptionSx = {
  ...surveyQuestionAnswersSx,
  mt: 0,
} as const

/** Figma 5074-13238 — required validation below answer controls */
export const surveyQuestionRequiredErrorSx = {
  m: 0,
  mt: '24px',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '24px',
  color: surveyExecRequired,
  textAlign: 'center',
  width: '100%',
} as const

/** @deprecated use surveyQuestionCardShellSx — completion / error states */
export const surveyQuestionCardSx = {
  ...surveyQuestionCardShellSx,
  px: { xs: 2.5, sm: '52px' },
  py: { xs: 3, sm: '30px' },
} as const

/** Figma 5074-13203 — QUESTION N eyebrow */
export const surveyQuestionEyebrowSx = {
  m: 0,
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: '13px',
  lineHeight: '19.5px',
  color: surveyExecEyebrow,
  textTransform: 'uppercase',
} as const

export const surveyQuestionPromptSx = {
  m: 0,
  mt: { xs: 1.5, sm: `${SURVEY_QUESTION_EYEBROW_TO_PROMPT_GAP_PX}px` },
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: '22px',
  lineHeight: '30px',
  color: surveyExecPromptText,
  maxWidth: '100%',
} as const

export const surveyQuestionRequiredMarkSx = {
  color: surveyExecRequired,
} as const

export const surveyQuestionDescriptionSx = {
  m: 0,
  mt: { xs: 1.5, sm: 2 },
  mb: { xs: 3, sm: 4 },
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '24px',
  color: surveyEntryMutedColor,
} as const

/** Slider questions — extra space between description and track (Figma 5074-13566). */
export const surveyQuestionDescriptionSliderSx = {
  ...surveyQuestionDescriptionSx,
  mb: { xs: 4, sm: 6 },
} as const

export const surveyQuestionAnswersAfterDescriptionSliderSx = {
  ...surveyQuestionAnswersAfterDescriptionSx,
  mt: { xs: 2, sm: 3 },
} as const

/** Figma 5074-13197 / 13271 — Yes/No row (gap 16px, 64px tiles) */
export const surveyYesNoRowSx = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: '16px',
  width: '100%',
  m: 0,
} as const

export const surveyYesNoColumnSx = {
  ...surveyYesNoRowSx,
  flexDirection: 'column',
} as const

export const surveyYesNoToggleRowSx = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2,
  width: '100%',
  maxWidth: '400px',
  mx: 'auto',
} as const

export const surveyYesNoToggleLabelSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: '16px',
  lineHeight: '24px',
  color: surveyExecOptionLabel,
} as const

/** Figma 5074-13198 / 13200 — unselected option */
export const surveyYesNoOptionSx = {
  flex: 1,
  minWidth: 0,
  height: '64px',
  borderRadius: '12px',
  border: `1.5px solid ${surveyExecBorder}`,
  bgcolor: surfaceAuthCard,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: '16px',
  lineHeight: '24px',
  color: surveyExecOptionLabel,
  p: 0,
  transition: 'border-color 0.15s, background-color 0.15s, color 0.15s',
  '&:hover': {
    bgcolor: surveyExecOptionHover,
  },
  '&:focus-visible': {
    outline: `2px solid ${surveyExecPrimary}`,
    outlineOffset: 2,
  },
} as const

/** Figma 5074-13272 / 13268 — selected option (primary fill, white label) */
export const surveyYesNoOptionSelectedSx = {
  bgcolor: surveyExecPrimary,
  border: `1.5px solid ${surveyExecYesNoSelectedBorder}`,
  color: surveyExecOnAccent,
  '&:hover': {
    bgcolor: surveyExecPrimary,
    opacity: 0.92,
  },
} as const

/** Figma 5076-7238 / 5076-7254 — multi-select (checkbox) option list */
export const surveyCheckboxListSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  width: '100%',
  m: 0,
} as const

export const surveyChoiceListHorizontalSx = {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: '10px',
  width: '100%',
  m: 0,
  '& > button': {
    width: { xs: '100%', sm: 'auto' },
    flex: { xs: '1 1 100%', sm: '1 1 auto' },
    minWidth: { sm: 120 },
  },
} as const

export const surveyChoiceListGridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
  gap: '10px',
  width: '100%',
  m: 0,
} as const

export const surveyOtherSpecifyWrapSx = {
  mt: 2,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
} as const

/** Figma 312:4731 — specify field label */
export const surveyOtherSpecifyLabelSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: '20px',
  color: surveyExecOptionLabel,
} as const

/** Figma 312:4731 — 56px white input, #e2e8f0 border, radius 10 */
export const surveyOtherSpecifyInputSx = {
  '& .MuiOutlinedInput-root': {
    height: '56px',
    bgcolor: '#ffffff',
    borderRadius: '10px',
    fontFamily: 'Inter, sans-serif',
    fontSize: '14px',
    fontWeight: 500,
    color: surveyExecOptionLabel,
    '& fieldset': { borderColor: surveyExecBorder },
    '&:hover fieldset': { borderColor: surveyExecBorder },
    '&.Mui-focused fieldset': { borderColor: surveyExecBrandBlue, borderWidth: '1.5px' },
  },
  '& .MuiOutlinedInput-input': {
    px: '16px',
    '&::placeholder': { color: '#a0abbf', opacity: 1 },
  },
} as const

/** Figma 312:4731 — `6 / 150` counter */
export const surveyOtherSpecifyCounterSx = {
  alignSelf: 'flex-end',
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  lineHeight: '16px',
  color: '#64748b',
} as const

export const surveyOtherSpecifyErrorSx = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  lineHeight: '16px',
  color: surveyExecRequired,
} as const

/** Figma 5074-13697 — unselected checkbox row */
export const surveyCheckboxRowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
  height: '52px',
  minHeight: '52px',
  px: '19.5px',
  py: '1.5px',
  boxSizing: 'border-box',
  borderRadius: '10px',
  border: `1.5px solid ${surveyExecBorder}`,
  bgcolor: '#ffffff',
  cursor: 'pointer',
  width: '100%',
  textAlign: 'left',
  transition: 'border-color 0.15s, background-color 0.15s',
  '&:hover': {
    bgcolor: surveyExecOptionHover,
  },
  '&:focus-visible': {
    outline: `2px solid ${surveyExecPrimary}`,
    outlineOffset: 2,
  },
  '&:disabled': {
    cursor: 'not-allowed',
    opacity: 0.5,
  },
} as const

/** Figma 5074-13949 — selected checkbox row */
export const surveyCheckboxRowSelectedSx = {
  bgcolor: surveyExecOptionHover,
  border: `1.5px solid ${surveyExecPrimary}`,
  '&:hover': {
    bgcolor: surveyExecOptionHover,
  },
} as const

export const surveyCheckboxOptionLabelSx = {
  flex: 1,
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '15px',
  lineHeight: '22.5px',
  color: surveyExecOptionLabel,
} as const

export const surveyCheckboxControlSx = {
  p: 0,
  width: 24,
  height: 24,
  flexShrink: 0,
  color: surveyExecBorder,
  '&.Mui-checked': {
    color: surveyExecPrimary,
  },
  '& .MuiSvgIcon-root': {
    fontSize: 24,
  },
} as const

export const surveyCheckboxSelectionHintSx = {
  mt: 1.5,
  mx: 0,
  fontFamily: 'Inter, sans-serif',
  fontSize: '16px',
  lineHeight: '28px',
  color: '#64748b',
} as const

/** Figma 5074-13576 — single select (aliases checkbox option tokens) */
export const surveyOptionListSx = surveyCheckboxListSx
export const surveyOptionRowSx = surveyCheckboxRowSx
export const surveyOptionRowSelectedSx = surveyCheckboxRowSelectedSx
export const surveyOptionLabelSx = surveyCheckboxOptionLabelSx

/** Figma 5074-13308 — dropdown trigger */
export const surveyDropdownPlaceholderColor = '#a0abbf'

export const surveyDropdownFieldSx = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    height: '56px',
    minHeight: '56px',
    borderRadius: '10px',
    bgcolor: '#ffffff',
    fontFamily: 'Inter, sans-serif',
    '& fieldset': {
      borderColor: surveyExecBorder,
      borderWidth: '1px',
    },
    '&:hover fieldset': {
      borderColor: surveyExecBorder,
    },
    '&.Mui-focused fieldset': {
      borderColor: surveyExecPrimary,
      borderWidth: '1px',
    },
  },
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    py: 0,
    pl: '20px',
    pr: '48px !important',
    minHeight: '54px !important',
    boxSizing: 'border-box',
  },
} as const

export const surveyDropdownPlaceholderSx = {
  color: surveyDropdownPlaceholderColor,
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '21px',
  letterSpacing: '-0.1504px',
} as const

export const surveyDropdownValueSx = {
  color: surveyExecOptionLabel,
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '21px',
  letterSpacing: '-0.1504px',
} as const

/** Figma 5074-13371 — short text single-line field */
export const surveyShortTextRootSx = {
  width: '100%',
  maxWidth: 649,
  mx: 'auto',
} as const

export const surveyShortTextFieldSx = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    height: 56,
    minHeight: 56,
    borderRadius: '10px',
    bgcolor: '#ffffff',
    fontFamily: 'Inter, sans-serif',
    '& fieldset': {
      borderColor: surveyExecBorder,
      borderWidth: '1px',
    },
    '&:hover fieldset': {
      borderColor: surveyExecBorder,
    },
    '&.Mui-focused fieldset': {
      borderColor: surveyExecPrimary,
      borderWidth: '1px',
    },
  },
  '& .MuiOutlinedInput-input': {
    py: 0,
    pl: '20px',
    pr: '15px',
    height: '54px',
    boxSizing: 'border-box',
    fontFamily: 'Inter, sans-serif',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '21px',
    letterSpacing: '-0.1504px',
    color: surveyExecPromptText,
    '&::placeholder': {
      color: surveyDropdownPlaceholderColor,
      opacity: 1,
    },
  },
} as const

export const surveyShortTextCounterRowSx = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-end',
  width: '100%',
  mt: 1,
} as const

export const surveyShortTextCounterSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '12px',
  lineHeight: '16px',
  color: '#64748b',
} as const

/** Figma 5074-13408 — long text multiline field */
export const surveyLongTextRootSx = {
  width: '100%',
  maxWidth: 649,
  mx: 'auto',
} as const

export const surveyLongTextFieldSx = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    minHeight: 109,
    borderRadius: '10px',
    bgcolor: '#ffffff',
    alignItems: 'flex-start',
    fontFamily: 'Inter, sans-serif',
    '& fieldset': {
      borderColor: surveyExecBorder,
      borderWidth: '1px',
    },
    '&:hover fieldset': {
      borderColor: surveyExecBorder,
    },
    '&.Mui-focused fieldset': {
      borderColor: surveyExecPrimary,
      borderWidth: '1px',
    },
  },
  '& .MuiOutlinedInput-input': {
    py: '12px',
    px: '20px',
    boxSizing: 'border-box',
    fontFamily: 'Inter, sans-serif',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '21px',
    letterSpacing: '-0.1504px',
    color: surveyExecPromptText,
    textAlign: 'left',
    '&::placeholder': {
      color: surveyDropdownPlaceholderColor,
      opacity: 1,
      fontWeight: 500,
      textAlign: 'left',
    },
  },
} as const

export const surveyLongTextFooterRowSx = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  width: '100%',
  mt: 1,
  gap: 2,
} as const

export const surveyLongTextMetaSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '12px',
  lineHeight: '16px',
  color: '#64748b',
} as const

/** Figma 5074-13335 — date picker trigger (56px, matches dropdown / short text). */
export const surveyDatePickerRootSx = {
  width: '100%',
  maxWidth: 649,
  mx: 'auto',
} as const

export const surveyDateHintLabelSx = {
  display: 'block',
  mb: 0.5,
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '21px',
  letterSpacing: '-0.1504px',
  color: surveyExecEyebrow,
} as const

const surveyDatePickerOpenButtonSx = {
  p: '4px',
  mr: '4px',
  color: '#4a5565',
  borderRadius: '4px',
  bgcolor: 'transparent',
  '&:hover': { bgcolor: 'rgba(15, 23, 42, 0.06)' },
  '&.Mui-focusVisible': {
    outline: `2px solid ${surveyExecPrimary}`,
    outlineOffset: 2,
  },
  '&.Mui-disabled': { opacity: 0.55 },
} as const

export const surveyDatePickerFieldSx = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    height: '56px',
    minHeight: '56px',
    borderRadius: '10px',
    bgcolor: '#ffffff',
    alignItems: 'center',
    pr: '4px',
    fontFamily: 'Inter, sans-serif',
    '& fieldset': {
      borderColor: surveyExecBorder,
      borderWidth: '1px',
    },
    '&:hover fieldset': {
      borderColor: surveyExecBorder,
    },
    '&.Mui-focused fieldset': {
      borderColor: surveyExecPrimary,
      borderWidth: '1px',
    },
  },
  '& .MuiOutlinedInput-input': {
    py: 0,
    pl: '20px',
    pr: '8px',
    boxSizing: 'border-box',
    fontFamily: 'Inter, sans-serif',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '21px',
    letterSpacing: '-0.1504px',
    color: surveyExecPromptText,
    '&::placeholder': {
      color: surveyDropdownPlaceholderColor,
      opacity: 1,
      fontStyle: 'italic',
    },
  },
  '& .MuiInputAdornment-root.MuiInputAdornment-positionEnd': {
    marginLeft: 0,
    marginRight: 0,
    maxHeight: 'none',
    height: 'auto',
    alignSelf: 'center',
  },
  '& .MuiInputAdornment-root .MuiIconButton-root': surveyDatePickerOpenButtonSx,
} as const

export const surveyDatePickerCalendarSlotProps = {
  desktopPaper: {
    sx: {
      borderRadius: '10px',
      border: `1px solid ${surveyExecBorder}`,
      boxShadow: '0 12px 40px rgba(15, 23, 42, 0.12)',
      overflow: 'hidden',
    },
  },
  layout: {
    sx: {
      '& .MuiPickersDay-root': { fontSize: '14px' },
      '& .MuiPickersDay-root.Mui-selected': {
        bgcolor: surveyExecPrimary,
        color: surveyExecOnAccent,
        fontWeight: 600,
        '&:hover, &.Mui-focusVisible': { bgcolor: surveyExecPrimary },
      },
      '& .MuiPickersDay-root:not(.Mui-selected).MuiPickersDay-today': {
        border: `1px solid ${surveyExecPrimary}`,
        fontWeight: 600,
      },
    },
  },
  openPickerIcon: {
    sx: { fontSize: 20, color: '#4a5565', display: 'block' },
  },
  openPickerButton: { sx: surveyDatePickerOpenButtonSx },
} as const

/** Figma 5074-13501 — Likert horizontal scale */
export const surveyLikertBorder = '#cbd5e1'

export const surveyLikertRootSx = {
  width: '100%',
  maxWidth: '631px',
  mx: 'auto',
} as const

export const surveyLikertHorizontalRowSx = {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'nowrap',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: { xs: '4px', sm: '8px' },
  width: '100%',
  overflowX: { xs: 'auto', sm: 'visible' },
  WebkitOverflowScrolling: 'touch',
} as const

export const surveyLikertColumnSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '10px',
  flex: '1 1 0',
  minWidth: 0,
  maxWidth: 'none',
} as const

export const surveyLikertRadioButtonSx = {
  p: 0,
  m: 0,
  border: 'none',
  bgcolor: 'transparent',
  cursor: 'pointer',
  lineHeight: 0,
  display: 'inline-flex',
  '&:focus-visible': {
    outline: `2px solid ${surveyExecPrimary}`,
    outlineOffset: 2,
    borderRadius: '22px',
  },
} as const

export const surveyLikertRadioUnselectedSx = {
  width: 44,
  height: 44,
  borderRadius: '22px',
  border: `1.5px solid ${surveyLikertBorder}`,
  bgcolor: '#ffffff',
  flexShrink: 0,
  boxSizing: 'border-box',
} as const

export const surveyLikertRadioSelectedSx = {
  width: 44,
  height: 44,
  borderRadius: '22px',
  border: `1.5px solid ${surveyExecPrimary}`,
  bgcolor: surveyExecPrimary,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  boxSizing: 'border-box',
} as const

export const surveyLikertRadioInnerDotSx = {
  width: 16,
  height: 16,
  borderRadius: '50%',
  bgcolor: '#ffffff',
} as const

export const surveyLikertLabelSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '12px',
  lineHeight: '18px',
  color: '#6b7a90',
  textAlign: 'center',
  width: '100%',
  wordBreak: 'break-word',
} as const

export const surveyLikertVerticalStackSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  width: '100%',
  maxWidth: 480,
  mx: 'auto',
} as const

export const surveyLikertVerticalRowSx = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 1.5,
  width: '100%',
  textAlign: 'left',
} as const

export const surveyLikertVerticalLabelSx = {
  ...surveyLikertLabelSx,
  textAlign: 'left',
  flex: 1,
} as const

/** Figma 5074-13566 / 5076-7336 — numeric slider */
export const surveySliderRootSx = {
  width: '100%',
  maxWidth: 658,
  mx: 'auto',
  pt: 0.5,
  pb: 0.5,
} as const

export const surveySliderTrackRail = '#e4e9f0'

export const surveySliderSx = {
  width: '100%',
  height: 6,
  py: 2.5,
  '& .MuiSlider-rail': {
    height: 6,
    opacity: 1,
    bgcolor: surveySliderTrackRail,
    borderRadius: '16777200px',
  },
  '& .MuiSlider-track': {
    height: 6,
    border: 'none',
    bgcolor: surveyExecPrimary,
    borderRadius: '16777200px',
  },
  '& .MuiSlider-thumb': {
    width: 20,
    height: 20,
    bgcolor: surveyExecPrimary,
    border: '2px solid #ffffff',
    boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1), 0px 2px 4px rgba(0, 0, 0, 0.1)',
    '&:hover, &.Mui-focusVisible, &.Mui-active': {
      boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1), 0px 2px 4px rgba(0, 0, 0, 0.1)',
    },
  },
  '& .MuiSlider-valueLabel': {
    fontFamily: 'Inter, sans-serif',
    fontSize: '24px',
    fontWeight: 700,
    lineHeight: '21px',
    letterSpacing: '-0.1504px',
    color: surveyExecPrimary,
    bgcolor: 'transparent',
    top: -32,
    '&::before': { display: 'none' },
  },
} as const

export const surveySliderEndLabelsRowSx = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  width: '100%',
  mt: 1.5,
  columnGap: 2,
} as const

export const surveySliderEndLabelSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '13px',
  lineHeight: '19.5px',
  letterSpacing: '-0.0762px',
  color: '#6b7a90',
  maxWidth: '48%',
  wordBreak: 'break-word',
} as const

export const surveySliderEndLabelStartSx = {
  ...surveySliderEndLabelSx,
  textAlign: 'left',
} as const

export const surveySliderEndLabelEndSx = {
  ...surveySliderEndLabelSx,
  textAlign: 'right',
  ml: 'auto',
} as const

export const surveySliderInvalidSx = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  lineHeight: '21px',
  color: surveyExecEtaMuted,
} as const

/** Figma 5076-7349 — drag-to-rank list */
export const surveyRankingRootSx = {
  width: '100%',
  maxWidth: 678,
  mx: 'auto',
} as const

export const surveyRankingHintSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '12px',
  lineHeight: '16px',
  color: '#3b82f6',
  mb: 1.25,
} as const

export const surveyRankingListSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  width: '100%',
} as const

export const surveyRankingRowSx = {
  bgcolor: '#ffffff',
  border: '1.5px solid #e2e8f0',
  borderRadius: '10px',
  minHeight: 52,
  height: 52,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '14px',
  px: '19.5px',
  py: '1.5px',
  boxSizing: 'border-box',
  width: '100%',
} as const

export const surveyRankingDragHandleSx = {
  color: '#cbd5e1',
  p: 0,
  flexShrink: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'grab',
  border: 'none',
  bgcolor: 'transparent',
  lineHeight: 0,
  '&:active': { cursor: 'grabbing' },
  '&:focus-visible': {
    outline: `2px solid ${surveyExecPrimary}`,
    outlineOffset: 2,
    borderRadius: '4px',
  },
} as const

export const surveyRankingBadgeSx = {
  width: 28,
  height: 28,
  borderRadius: '50%',
  bgcolor: surveyExecPrimary,
  color: surveyExecOnAccent,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: '12px',
  lineHeight: '16px',
} as const

export const surveyRankingLabelSx = {
  flex: 1,
  minWidth: 0,
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '20px',
  color: '#0f1729',
  textAlign: 'left',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const

export const surveyRankingThumbSx = {
  width: 38,
  height: 38,
  borderRadius: '5px',
  objectFit: 'contain',
  bgcolor: '#f1f5f9',
  flexShrink: 0,
} as const

export const surveyRankingInvalidSx = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  lineHeight: '21px',
  color: surveyExecEtaMuted,
} as const

/** Figma 5074-13843 / Sensemaker QuestionLivePreview — picture choice grid */
export const surveyPictureChoiceRootSx = {
  width: '100%',
  maxWidth: 678,
  mx: 'auto',
} as const

export const surveyPictureChoiceGridSx = {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: '19px',
  width: '100%',
} as const

/** Figma 4399:8965 — picture choice card; selection = border only (no radio dot). */
export const surveyPictureChoiceCardSx = {
  bgcolor: '#ffffff',
  borderRadius: '10px',
  p: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: '8px',
  m: 0,
  overflow: 'hidden',
  border: `3px solid ${surveyExecBorder}`,
  cursor: 'pointer',
  textAlign: 'left',
  font: 'inherit',
  boxSizing: 'border-box',
  '&:focus-visible': {
    outline: `2px solid ${surveyExecPrimary}`,
    outlineOffset: 2,
  },
} as const

export const surveyPictureChoiceCardSelectedSx = {
  border: `3px solid ${surveyExecPrimary}`,
} as const

export const surveyPictureChoiceImageSx = {
  display: 'block',
  objectFit: 'cover',
  borderRadius: 0,
  bgcolor: '#f0f8fd',
  flexShrink: 0,
} as const

export const surveyPictureChoiceCaptionSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '12px',
  lineHeight: '16px',
  color: '#64748b',
  textAlign: 'center',
  width: '100%',
  px: '8px',
  pb: '8px',
  wordBreak: 'break-word',
} as const

export const surveyPictureChoiceInvalidSx = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  lineHeight: '21px',
  color: surveyExecEtaMuted,
} as const

/** Figma 5074-13452 — rating stars row */
export const surveyRatingRootSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  maxWidth: '741px',
  mx: 'auto',
} as const

/** Stacks rating star rows (max 5 per row; partial last row centered). */
export const surveyRatingStarsRowsStackSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: { xs: '16px', sm: '24px' },
  width: '100%',
} as const

export const surveyRatingStarsRowSx = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  flexWrap: 'nowrap',
  gap: { xs: '24px', sm: '56px' },
  width: '100%',
  minHeight: '56px',
  overflowX: { xs: 'auto', sm: 'visible' },
} as const

/** Stacks numeric rating button rows (same 5-per-row rule as stars). */
export const surveyRatingNumbersRowsStackSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px',
  width: '100%',
} as const

export const surveyRatingNumbersRowSx = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  flexWrap: 'nowrap',
  gap: '8px',
  width: '100%',
} as const

export const surveyRatingStarButtonSx = {
  p: 0,
  m: 0,
  border: 'none',
  bgcolor: 'transparent',
  cursor: 'pointer',
  lineHeight: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:focus-visible': {
    outline: `2px solid ${surveyExecPrimary}`,
    outlineOffset: 2,
    borderRadius: '4px',
  },
} as const

export const surveyRatingNumberButtonSx = {
  width: 32,
  height: 32,
  minWidth: 32,
  borderRadius: '10px',
  border: `1px solid ${surveyExecBorder}`,
  bgcolor: '#ffffff',
  color: surveyExecPromptText,
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: '21px',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  p: 0,
  transition: 'border-color 0.15s, background-color 0.15s, color 0.15s',
  '&:focus-visible': {
    outline: `2px solid ${surveyExecPrimary}`,
    outlineOffset: 2,
  },
} as const

export const surveyRatingNumberButtonSelectedSx = {
  border: `2px solid ${surveyExecPrimary}`,
  bgcolor: surveyExecPrimary,
  color: surveyExecOnAccent,
} as const

export const surveyRatingScaleLabelsRowSx = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  width: '100%',
  mt: '16px',
  columnGap: 2,
} as const

export const surveyRatingScaleLabelSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: '12px',
  lineHeight: '16px',
  letterSpacing: '0.12px',
  color: surveyExecEtaMuted,
  maxWidth: '45%',
  wordBreak: 'break-word',
} as const

export const surveyRatingScaleLabelStartSx = {
  ...surveyRatingScaleLabelSx,
  textAlign: 'left',
} as const

export const surveyRatingScaleLabelEndSx = {
  ...surveyRatingScaleLabelSx,
  textAlign: 'right',
  ml: 'auto',
} as const

/** Figma 5074-13466 — "N out of M" below selection */
export const surveyRatingValueCaptionSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: '12px',
  lineHeight: '16px',
  letterSpacing: '0.12px',
  color: surveyExecPrimary,
  mt: 1.5,
  width: '100%',
  textAlign: 'left',
} as const

export const surveyDropdownMenuPaperSx = {
  mt: 0.5,
  borderRadius: '10px',
  border: `1px solid ${surveyExecBorder}`,
  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
  '& .MuiMenuItem-root': {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '21px',
    color: surveyExecOptionLabel,
    minHeight: 44,
    '&.Mui-selected': {
      bgcolor: surveyExecOptionHover,
      '&:hover': {
        bgcolor: surveyExecOptionHover,
      },
    },
    '&:hover': {
      bgcolor: surveyExecOptionHover,
    },
  },
} as const

/** Figma 5076:7210 — Previous + Next below question card (768px row). */
export const surveyNavRowSx = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: { xs: 'wrap', sm: 'nowrap' },
  gap: { xs: 1, sm: 0 },
  width: '100%',
  minHeight: '40px',
} as const

/** Figma 5074-13208 — 16×16 arrow-left asset */
export const surveyNavPreviousIconSx = {
  display: 'block',
  width: 16,
  height: 16,
  flexShrink: 0,
  color: surveyExecNavPrevious,
} as const

export const surveyBackButtonSx = {
  minHeight: '40px',
  px: '24px',
  py: '12px',
  borderRadius: '8px',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: '16px',
  lineHeight: '24px',
  textTransform: 'none',
  color: surveyExecNavPrevious,
  border: 'none',
  bgcolor: 'transparent',
  boxShadow: 'none',
  gap: '8px',
  '& .MuiButton-startIcon': {
    marginRight: '8px',
    marginLeft: 0,
  },
  '&:hover': {
    bgcolor: 'rgba(77, 93, 136, 0.08)',
    border: 'none',
    boxShadow: 'none',
  },
} as const

export const surveyNextButtonSx = {
  minWidth: { xs: 0, sm: '120px' },
  flex: { xs: 1, sm: '0 0 auto' },
  minHeight: '40px',
  px: '16px',
  py: '10px',
  borderRadius: '8px',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '0.1px',
  textTransform: 'none',
  bgcolor: surveyExecPrimary,
  color: surveyExecOnAccent,
  boxShadow: 'none',
  '&:hover': {
    bgcolor: surveyExecPrimary,
    color: surveyExecOnAccent,
    opacity: 0.92,
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    bgcolor: '#94a3b8',
    color: '#fff',
  },
} as const

/** Optional — completion stub on execution column */
export const surveyExecutionCompleteCardSx = {
  ...surveyQuestionCardShellSx,
  px: { xs: 2.5, sm: '52px' },
  py: { xs: 3, sm: '30px' },
  p: 4,
} as const

/** Figma 5074-14008 — submit success modal */
export const surveySubmitSuccessDialogPaperSx = {
  width: '100%',
  maxWidth: '448px',
  m: 2,
  borderRadius: '10px',
  overflow: 'hidden',
  boxShadow:
    '0px 20px 12.5px rgba(0, 0, 0, 0.1), 0px 8px 5px rgba(0, 0, 0, 0.1)',
} as const

export const surveySubmitSuccessDialogContentSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  p: 0,
} as const

export const surveySubmitSuccessIconWrapSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  pt: '32px',
  pb: 0,
  boxSizing: 'border-box',
} as const

export const surveySubmitSuccessIconCircleSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 64,
  height: 64,
  borderRadius: '50%',
  bgcolor: '#f0fdf4',
  color: '#15803d',
} as const

export const surveySubmitSuccessTitleSx = {
  m: 0,
  mt: 0,
  mb: 0,
  px: 3,
  pt: 0,
  pb: 0,
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: '20px',
  lineHeight: '28px',
  letterSpacing: '-0.4492px',
  color: '#23262f',
  textAlign: 'center',
} as const

export const surveySubmitSuccessBodySx = {
  m: 0,
  px: 3,
  pt: 1.5,
  pb: 0,
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '22.75px',
  letterSpacing: '-0.1504px',
  color: '#4a5565',
  textAlign: 'center',
} as const

export const surveySubmitSuccessActionsSx = {
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  px: { xs: 3, sm: '150px' },
  py: 3,
  boxSizing: 'border-box',
} as const

export const surveySubmitSuccessOkButtonSx = {
  minWidth: '148px',
  minHeight: '40px',
  px: 2,
  py: 1.5,
  borderRadius: '12px',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: '16px',
  textTransform: 'none',
  bgcolor: SP_SIGNUP_YELLOW,
  backgroundColor: SP_SIGNUP_YELLOW,
  color: SP_SIGNUP_NAVY,
  boxShadow: 'none',
  '&:hover': {
    bgcolor: SP_SIGNUP_YELLOW_HOVER,
    backgroundColor: SP_SIGNUP_YELLOW_HOVER,
    color: SP_SIGNUP_NAVY,
    opacity: 1,
    boxShadow: 'none',
  },
} as const

/** Figma 5076:7406 — vertically centered completion on #f8f9ff canvas (not question frame). */
export const surveyCompletionPageWrapSx = {
  flex: '1 1 auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  minHeight: { xs: 'min(100dvh - 80px, 900px)', sm: 'min(100dvh - 88px, 900px)' },
  boxSizing: 'border-box',
  bgcolor: surfaceAuthCanvas,
  backgroundColor: surfaceAuthCanvas,
  py: { xs: 3, sm: 4 },
  px: { xs: 2, sm: 3 },
} as const

/** Trophy + card column — no entry-page 135px inset (that squeezed the 640px card). */
export const surveyCompletionRootSx = {
  width: '100%',
  maxWidth: SURVEY_COMPLETE_CARD_MAX_WIDTH_PX,
  mx: 'auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  boxSizing: 'border-box',
  px: 0,
} as const

/** Celebration art — fully inside the card (no negative overlap outside border). */
export const surveyCompletionIllustrationSx = {
  width: '100%',
  maxWidth: `${SURVEY_COMPLETE_ILLUSTRATION_MAX_WIDTH_PX}px`,
  height: { xs: '120px', sm: `${SURVEY_COMPLETE_ILLUSTRATION_HEIGHT_PX}px` },
  mx: 'auto',
  mb: { xs: 1.5, sm: 2 },
  flexShrink: 0,
  pointerEvents: 'none',
} as const

export const surveyCompletionCelebrationImgSx = {
  display: 'block',
  width: '100%',
  height: '100%',
  objectFit: 'contain',
} as const

/** Figma 5074-13988 — completion card (#f8f9ff, upward shadow, 640px). */
export const surveyCompletionCardSx = {
  position: 'relative',
  width: '100%',
  minHeight: 'auto',
  bgcolor: surfaceAuthCanvas,
  backgroundColor: surfaceAuthCanvas,
  border: '1px solid #bfc7d2',
  borderRadius: `${SURVEY_ENTRY_CARD_RADIUS_PX}px`,
  boxShadow: '0px -4px 12px 0px rgba(4, 24, 63, 0.05)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  overflow: 'hidden',
  pt: { xs: '20px', sm: `${SURVEY_COMPLETE_CARD_VERTICAL_INSET_PX}px` },
  pb: { xs: '20px', sm: `${SURVEY_COMPLETE_CARD_VERTICAL_INSET_PX}px` },
  px: { xs: '12px', sm: `${SURVEY_COMPLETE_CARD_PADDING_PX}px` },
  boxSizing: 'border-box',
  zIndex: 1,
} as const

export const surveyCompletionCardBodySx = {
  width: '100%',
  maxWidth: `${SURVEY_COMPLETE_INNER_MAX_WIDTH_PX}px`,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  boxSizing: 'border-box',
} as const

export const surveyCompletionTitleSx = {
  m: 0,
  mb: 0.5,
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: { xs: '28px', sm: '32px' },
  lineHeight: { xs: '36px', sm: '40px' },
  letterSpacing: '0.4063px',
  color: surveyEntryHeadingColor,
  textAlign: 'center',
} as const

export const surveyCompletionSubtitleSx = {
  m: 0,
  mb: { xs: 4, sm: 5 },
  maxWidth: `${SURVEY_COMPLETE_INNER_MAX_WIDTH_PX}px`,
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '18px',
  lineHeight: '28.8px',
  color: '#3f4850',
  textAlign: 'center',
} as const

export const surveyCompletionRewardCardSx = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: { xs: 1, sm: 2 },
  width: '100%',
  maxWidth: `${SURVEY_COMPLETE_INNER_MAX_WIDTH_PX}px`,
  minHeight: { xs: 0, sm: '151px' },
  p: { xs: '16px', sm: '24px' },
  mb: { xs: 3, sm: 4 },
  borderRadius: '12px',
  border: '1px solid #f59e0b',
  bgcolor: '#fff6e6',
  backgroundColor: '#fff6e6',
  boxSizing: 'border-box',
} as const

export const surveyCompletionRewardTextSx = {
  flex: '1 1 auto',
  minWidth: 0,
  pr: { xs: 0, sm: 1 },
} as const

export const surveyCompletionRewardHeadingSx = {
  m: 0,
  mb: 0,
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: { xs: '20px', sm: '24px' },
  lineHeight: { xs: '28px', sm: '40px' },
  letterSpacing: '0.4063px',
  color: surveyEntryHeadingColor,
  overflowWrap: 'normal',
  wordBreak: 'normal',
} as const

export const surveyCompletionRewardEarnedSx = {
  m: 0,
  display: 'block',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: { xs: '24px', sm: '32px' },
  lineHeight: { xs: '32px', sm: '40px' },
  letterSpacing: '0.4063px',
  color: SP_SIGNUP_YELLOW,
} as const

export const surveyCompletionRewardBodySx = {
  m: 0,
  mt: '3px',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '19.6px',
  letterSpacing: '0.14px',
  color: '#35466e',
  overflowWrap: 'normal',
  wordBreak: 'normal',
} as const

/** Figma 136:3402 — guest completion body stays Inter Regular. */
export const surveyCompletionRewardBodyGuestSx = {
  ...surveyCompletionRewardBodySx,
  fontWeight: 400,
} as const

export const surveyCompletionBadgeImgSx = {
  display: 'block',
  width: { xs: 64, sm: 100 },
  height: { xs: 64, sm: 100 },
  flexShrink: 0,
  objectFit: 'contain',
} as const

export const surveyCompletionActionsRowSx = {
  display: 'grid',
  width: '100%',
  boxSizing: 'border-box',
  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
  gap: { xs: 2, sm: '14px' },
  alignItems: 'stretch',
} as const

/** No-reward completion — single primary Home centered (Figma 5074-14025 pattern). */
export const surveyCompletionActionsRowNoRewardSx = {
  display: 'flex',
  width: '100%',
  justifyContent: 'center',
  boxSizing: 'border-box',
} as const

export const surveyCompletionClaimButtonSx = {
  width: '100%',
  minWidth: 0,
  minHeight: '56px',
  px: 3,
  py: 2,
  borderRadius: '8px',
  fontFamily: 'Roboto, Inter, sans-serif',
  fontWeight: 500,
  fontSize: '16px',
  lineHeight: '24px',
  letterSpacing: '0.15px',
  textTransform: 'none',
  bgcolor: SP_SIGNUP_YELLOW,
  backgroundColor: SP_SIGNUP_YELLOW,
  color: SP_SIGNUP_NAVY,
  boxShadow: 'none',
  '&:hover': {
    bgcolor: SP_SIGNUP_YELLOW_HOVER,
    backgroundColor: SP_SIGNUP_YELLOW_HOVER,
    color: SP_SIGNUP_NAVY,
    opacity: 1,
    boxShadow: 'none',
  },
} as const

export const surveyCompletionHomeButtonSx = {
  width: '100%',
  minWidth: 0,
  minHeight: '56px',
  px: 2,
  py: 1.25,
  borderRadius: '8px',
  fontFamily: 'Roboto, Inter, sans-serif',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '0.1px',
  textTransform: 'none',
  color: surveyExecPrimary,
  borderColor: surveyExecPrimary,
  bgcolor: 'transparent',
  '&:hover': {
    bgcolor: 'rgba(0, 140, 208, 0.06)',
    borderColor: surveyExecPrimary,
  },
} as const

export const surveyCompletionHomeOnlyButtonSx = {
  ...surveyCompletionClaimButtonSx,
  width: '100%',
  maxWidth: { xs: '100%', sm: `${SURVEY_COMPLETE_BUTTON_WIDTH_PX}px` },
  fontSize: '16px',
  lineHeight: '24px',
  letterSpacing: '0.15px',
} as const
